import { useCallback, useEffect, useMemo, useState, type ReactNode, type ChangeEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { apiBaseUrl } from '../../api/client.js';
import { Button } from '../../../../shared/components/Button.js';
import { Can, useCan } from '../../shell/Can.js';
import {
  ColumnFilters,
  type FilterableColumn,
} from '../../../../shared/components/ColumnFilters.js';
import { orderLineQuantityLabel } from './orderLineQuantity.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import {
  loadReviewQueue,
  reviseLineQuantity,
  importReviewCsv,
  forwardLinesToVendor,
  attachWorkOrderToLine,
  type ReviewRow,
} from '../../api/brandingReviewApi.js';
import { rejectInstance } from '../../api/workflowApi.js';
import {
  listWorkOrderCover,
  type WorkOrderCoverLine,
} from '../../api/brandingWorkOrderApi.js';

/**
 * Branding team's review queue.
 *
 * The requested quantity is never edited — a revised figure is captured beside
 * it, and that revised figure is what goes to the vendor. Rows can be revised
 * one at a time here, or in bulk by downloading the queue, editing only the
 * Revised Qty column and uploading it back.
 */
export function BrandingReviewPage(): ReactNode {
  const { token } = useAuth();
  // Stored quantities and rates are written exactly, grouped the tenant's way.
  const { decimal } = useBusinessNumber();
  // The approver sees this same screen — the same rows, the same work order,
  // the same remaining balance — because he is approving exactly what the
  // officer prepared and must see it in the form it was prepared in. What
  // differs is what he may *do*, and that is decided by the grants his role
  // holds, never by a second copy of the page and never by a role name.
  const mayEdit = useCan('/admin/branding-review', 'edit');
  const [rows, setRows] = useState<readonly ReviewRow[] | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [draft, setDraft] = useState<Record<string, string>>({});
  /**
   * The rows the officer has opened for change.
   *
   * A row is read-only until he says otherwise. Leaving every field live meant
   * a stray keystroke altered a row he was only reading, and it left nothing
   * on a row that had already gone to the vendor — no control, no explanation,
   * just an empty cell where the others had buttons. Modify is that control,
   * and on a row that has left it is shown refusing rather than not shown.
   */
  const [modifying, setModifying] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  // The work orders that could cover a row, fetched only when asked for.
  const [cover, setCover] = useState<
    Readonly<Record<string, readonly WorkOrderCoverLine[] | 'loading'>>
  >({});
  /**
   * The rows left after the heading filters. Null until the filter panel has
   * reported once, so the table never renders an empty queue for an instant
   * before the first report arrives.
   */
  const [shown, setShown] = useState<readonly ReviewRow[] | null>(null);

  /**
   * Which headings can be narrowed, and what each row carries there.
   *
   * Only the columns whose values repeat are offered: a quantity differs on
   * every row, so a list of them would be a list of the rows themselves.
   */
  const filterColumns = useMemo<ReadonlyArray<FilterableColumn<ReviewRow>>>(
    () => [
      { key: 'dealer', label: 'Dealer', valueOf: (r) => r.companyName },
      { key: 'district', label: 'District', valueOf: (r) => r.districtCode },
      { key: 'type', label: 'Type', valueOf: (r) => r.typeName },
      { key: 'subtype', label: 'Sub-type', valueOf: (r) => r.itemName },
      // The Status column left the table but this outlived it: the three
      // statuses a queued row can hold are not the same thing — an approved row
      // can no longer be modified, an untouched one can. The filter lists each
      // with a count, which the table itself no longer shows.
      { key: 'status', label: 'Status', valueOf: (r) => r.lineStatusCode },
      {
        key: 'workorder',
        label: 'Work order',
        valueOf: (r) => (r.woNumber ? `${r.woNumber} / ${r.woLineNumber}` : null),
      },
      {
        key: 'vendor',
        label: 'Vendor',
        // The same order of authority the table and the server use.
        valueOf: (r) => r.woVendorName ?? r.vendorName,
      },
    ],
    [],
  );

  // Stable, so the filter panel reports only when the surviving set changes.
  const onFiltered = useCallback((v: readonly ReviewRow[]) => setShown(v), []);

  // A closed row shows only the revision actually saved: a blank here is the
  // blank the server refuses to forward ("this column is empty: Revised"), so
  // the screen must not dress it up as a figure. Opening the row offers the
  // requested quantity as the starting point to edit.
  const draftOf = (r: readonly ReviewRow[]): Record<string, string> =>
    Object.fromEntries(r.map((x) => [x.requestLineId, x.revisedQuantity ?? '']));

  const reload = async () => {
    const r = await loadReviewQueue(token);
    setRows(r);
    setDraft(draftOf(r));
  };

  useEffect(() => {
    let alive = true;
    loadReviewQueue(token)
      .then((r) => {
        if (!alive) return;
        setRows(r);
        setDraft(draftOf(r));
      })
      .catch((e) => { if (alive) { setRows([]); setError((e as Error).message); } });
    return () => { alive = false; };
  }, [token]);

  /**
   * What the table actually shows. `shown` is null until the filter panel has
   * reported once; the full queue stands in until then.
   */
  const visibleRows = useMemo(() => shown ?? rows ?? [], [shown, rows]);

  // Only rows still with the Branding team can be released — and only the ones
  // on screen. Select-all used to tick rows hidden by a filter, which would
  // send to a vendor work the officer could not see.
  //
  // The queue no longer delivers a row that has gone to the vendor, so this
  // normally matches every visible row. It is kept because a screen left open
  // in another tab still holds the rows it loaded, and must not offer to send
  // one the server has since released.
  const releasable = useMemo(
    () => visibleRows
      .filter((r) => ['FORWARDED', 'UNDER_REVIEW', 'APPROVED'].includes(r.lineStatusCode))
      .map((r) => r.requestLineId),
    [visibleRows],
  );

  /**
   * May this row be changed at all, and if not, why — in the server's own
   * words, so the screen and the server cannot disagree.
   *
   * The reason now arrives **with the row**. It used to be worked out here
   * from the row's status, which answers a different question: that column
   * records what the Branding Officer did to a line, not where its approval
   * has reached. So a line sitting with an approver was still offered a
   * Modify button, and the officer only learned otherwise when Forward
   * refused it — in different words, from a different rule.
   *
   * Who may change a record is decided by the tenant's own approval flow, and
   * this screen must not have an opinion about it. The owner's rule:
   * *"branding officer / vendor — ye kuch define mat karo, kyoki record
   * approval ke liye kiske paas jayega ye approval flow decide karega, aur
   * approval flow dynamic h."*
   *
   * The only thing still decided here is whether this caller may edit the
   * queue at all, which is a permission on the login rather than anything
   * about the row.
   */
  const whyNotModifiable = useCallback(
    (r: ReviewRow): string | null => {
      if (!mayEdit) return 'You may view this queue but not change it.';
      return r.whyNotModifiable ?? null;
    },
    [mayEdit],
  );

  /** The rows this caller may open for change. */
  const editable = useMemo(
    () =>
      visibleRows
        .filter((r) => whyNotModifiable(r) === null)
        .map((r) => r.requestLineId),
    [visibleRows, whyNotModifiable],
  );

  /** A row is only live while it is open for change. */
  const isOpen = useCallback(
    (id: string) => modifying.has(id) && editable.includes(id),
    [modifying, editable],
  );

  /**
   * Where each ticked row will go, and on whose authority — stated plainly,
   * because the one question an officer must never have to guess is which
   * vendor a record is actually going to.
   *
   * **The row is the instruction.** Its work order names a vendor, and a row
   * that has already been out keeps the one it went to; either way the Vendor
   * column shows him, and that is who it goes to. There is no second place to
   * name a vendor: while there was one, a row showing vendor X could be
   * released to vendor Y and nothing said so.
   */
  const eligible = useMemo(() => {
    // Only the ticked rows going to a vendor — a row still being approved goes
    // to its next approval level instead.
    const chosen = (rows ?? []).filter((r) => picked.has(r.requestLineId) && !r.canDecide);

    const tally = (
      list: readonly ReviewRow[],
      label: (r: ReviewRow) => string,
    ) => {
      const m = new Map<string, number>();
      for (const r of list) {
        const k = label(r);
        if (k) m.set(k, (m.get(k) ?? 0) + 1);
      }
      return [...m].map(([who, n]) => ({ who, rows: n }));
    };

    return {
      byOrder: tally(
        chosen.filter((r) => r.woVendorId),
        (r) =>
          r.woVendorName
            ? `${r.woVendorName}${r.woVendorCode ? ` (${r.woVendorCode})` : ''}`
            : (r.woVendorCode ?? ''),
      ),
      byRow: tally(
        chosen.filter((r) => !r.woVendorId && r.vendorId),
        (r) => r.vendorName ?? '',
      ),
      // A row whose Vendor column is empty is held back by the completeness
      // rule and says so; it is put right on this screen, not sent anyway.
      noVendor: chosen.filter((r) => !r.woVendorId && !r.vendorId).length,
    };
  }, [rows, picked]);

  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  /** Open one row for change, starting from what was requested when nothing is revised yet. */
  const openRow = (row: ReviewRow) => {
    setModifying((m) => new Set(m).add(row.requestLineId));
    setDraft((d) => ({
      ...d,
      [row.requestLineId]: row.revisedQuantity ?? row.quantity ?? '',
    }));
  };

  /**
   * Close a row again, putting back what it showed before it was opened.
   * Leaving a typed-but-unsaved figure on screen would have the row claiming a
   * revision that was never made.
   */
  const closeRow = (row: ReviewRow) => {
    setModifying((m) => {
      const next = new Set(m);
      next.delete(row.requestLineId);
      return next;
    });
    setDraft((d) => ({
      ...d,
      [row.requestLineId]: row.revisedQuantity ?? '',
    }));
  };

  const saveRevision = async (row: ReviewRow) => {
    const q = Number(draft[row.requestLineId]);
    if (!(q > 0)) { setError('Revised quantity must be greater than zero.'); return; }
    setBusy(true); setError(null); setStatus(null);
    try {
      await reviseLineQuantity(row.requestLineId, q, token);
      await reload();
      // The change is made; the row closes itself rather than staying open and
      // inviting another.
      setModifying((m) => {
        const next = new Set(m);
        next.delete(row.requestLineId);
        return next;
      });
      setStatus(`Row ${row.lineNumber} of ${row.requestNo} revised to ${q}`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  /**
   * A request raised by someone who does not issue work orders arrives without
   * one. The covering work orders are fetched per sub-type so the team can name
   * the commitment this line draws on before it goes to a vendor.
   */
  const loadCover = async (row: ReviewRow) => {
    if (!row.brandingItemId) return;
    const key = row.requestLineId;
    setCover((c) => ({ ...c, [key]: 'loading' }));
    try {
      const r = await listWorkOrderCover(row.brandingTypeId, row.brandingItemId, token, row.districtCode, row.requestLineId);
      setCover((c) => ({ ...c, [key]: r.data }));
    } catch { setCover((c) => ({ ...c, [key]: [] })); }
  };

  const attach = async (row: ReviewRow, workOrderLineId: string) => {
    setBusy(true); setError(null); setStatus(null);
    try {
      await attachWorkOrderToLine(row.requestLineId, workOrderLineId, token);
      await reload();
      setStatus(`Work order attached to row ${row.lineNumber} of ${row.requestNo}`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  // Owner, 19 Oct: every approval is decided here, by ticking rows. A ticked
  // row waiting at this reader's level goes down the engine's one approve /
  // reject path, so it moves to the next level the workflow defines and leaves
  // his page; a ticked row whose last level is approved goes to its vendor.
  const [remarks, setRemarks] = useState('');
  const decidable = useMemo(
    () => (rows ?? []).filter((r) => picked.has(r.requestLineId) && r.canDecide && r.workflowInstanceId),
    [rows, picked],
  );
  const rejectTicked = async (remark: string): Promise<{ done: string[]; failed: string[] }> => {
    const done: string[] = [];
    const failed: string[] = [];
    for (const row of decidable) {
      try {
        await rejectInstance(token, row.workflowInstanceId as string, remark);
        done.push(row.requestNo);
      } catch (e) { failed.push(`${row.requestNo}: ${(e as Error).message}`); }
    }
    return { done, failed };
  };
  const finish = async (said: string[], failed: string[]) => {
    try { await reload(); } catch (e) { failed.push((e as Error).message); }
    setPicked(new Set());
    setRemarks('');
    setStatus(said.length ? said.join(' ') : null);
    setError(failed.length ? failed.join(' · ') : null);
    setBusy(false);
  };

  const reject = async () => {
    const remark = remarks.trim();
    if (!remark) { setError('Give a reason to reject.'); return; }
    setBusy(true); setError(null); setStatus(null);
    const { done, failed } = await rejectTicked(remark);
    await finish(done.length ? [`Rejected: ${done.join(', ')}.`] : [], failed);
  };
  const download = async () => {
    setBusy(true); setError(null);
    try {
      // Fetched rather than linked so the Authorization header travels with it.
      const res = await fetch(
        `${apiBaseUrl}/v1/m17/branding-review/export${picked.size ? `?lineIds=${[...picked].join(',')}` : ''}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' },
      );
      if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'branding-review.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const upload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(null); setStatus(null);
    try {
      const text = await file.text();
      const r = await importReviewCsv(text, token);
      await reload();
      setStatus(`${r.updated} row${r.updated === 1 ? '' : 's'} updated`);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  // Owner, 19 Oct: one action for every ticked row, decided by the server's
  // own rule — every column filled, the work order able to carry it, a vendor
  // named. A complete row at this reader's level moves to its next approval
  // level; a complete, fully approved row goes to its vendor; an incomplete
  // row stays here with its reasons.
  const forwardNext = async () => {
    if (picked.size === 0) return;
    setBusy(true); setError(null); setStatus(null);
    const said: string[] = [];
    const failed: string[] = [];
    try {
      const r = await forwardLinesToVendor([...picked], token, remarks.trim() || undefined);
      if (r.advanced) said.push(`Forwarded ${r.advanced} row${r.advanced === 1 ? '' : 's'} to the next approval level.`);
      if (r.released) said.push(`Released ${r.released} row${r.released === 1 ? '' : 's'} to the vendor.`);
      // Each held row says why in its own words, and says which row it is.
      const held = r.held ?? [];
      if (held.length) {
        said.push(
          'Held back: ' +
            held.map((h) => `${h.requestNo ? `${h.requestNo} ` : ''}row ${h.lineNumber} — ${h.why}`).join('; '),
        );
      }
    } catch (e) { failed.push((e as Error).message); }
    await finish(said, failed);
  };

  if (!rows) return <p>Loading…</p>;

  // J3a: the administrator decides rows at any level, so he is told which level
  // each one waits on. The server sends it only to such a reader.

  return (
    <main aria-label="branding-review">
      <h1>Branding review</h1>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      {/*
        The spreadsheet exists so the officer can fill in two columns in bulk.
        The approver fills in nothing — he agrees or he does not — so handing
        him the file would be handing him a way to edit what he is meant to be
        judging. It is gated on the editing grant rather than on a role name,
        the same way every other action on this screen is decided.
      */}
      <Can path="/admin/branding-review" action="edit">
        <section aria-label="bulk-quantity">
          <h2>Bulk quantity revision</h2>
          <Button type="button" variant="secondary" onClick={() => void download()} disabled={busy} data-testid="download-csv">
            {picked.size > 0 ? `Download ${picked.size} selected` : 'Download all'}
          </Button>
          <label htmlFor="rv-upload">Upload edited file</label>
          <input id="rv-upload" type="file" accept=".csv,text/csv" onChange={(e) => void upload(e)} data-testid="upload-csv" />
          <p data-testid="upload-rule">
            Fill in <strong>Revised Qty</strong> and <strong>Work Order</strong> only.
            Change anything else and the whole file is refused — nothing is applied.
            Remaining quantity and vendor are not in the file; they follow the work
            order and appear here as soon as the rows come back.
          </p>
        </section>
      </Can>

      {/*
        The queue grows long, and the values worth narrowing by are already in
        it. The lists are built from these rows alone, they follow each other —
        pick a district and every other list narrows to what is left under it —
        and more than one value may be chosen in each.
      */}
      <ColumnFilters
        ariaLabel="queue-filters"
        columns={filterColumns}
        rows={rows}
        onChange={onFiltered}
      />

      {/* Fourteen columns do not fit a laptop screen, and the table had no
          scroll of its own — the work order, remaining quantity and vendor
          columns simply ran off the right edge with no way to reach them.
          The same wrapper WorkOrderDetailPage already uses. */}
      <div style={{ overflowX: 'auto' }}>
      <table aria-label="review-queue">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                aria-label="select-all-rows"
                data-testid="select-all"
                checked={releasable.length > 0 && releasable.every((id) => picked.has(id))}
                onChange={(e) => setPicked(new Set(e.target.checked ? releasable : []))}
                disabled={releasable.length === 0}
              />
            </th>
            {/* Row and Status are not shown. Row is the line's number inside
                its order, and every order in this queue has one line, so the
                column said "1" on every row and nothing else. Status was a
                code for what the row's own controls already show: a row that
                has gone to the vendor has no tick-box and its Modify refuses,
                saying why. The status itself is still read — it is the lock on
                both of those — it is simply no longer printed as a word. */}
            {/* Every column the server requires before a row may be forwarded
                carries the same mandatory mark as a form field. Pending at is
                the approval's own state, not something the officer fills. */}
            {['Request', 'Pending at', 'Dealer', 'District', 'Type', 'Sub-type',
              'Requested', 'Revised', 'Work order', 'Remaining qty', 'Vendor'].map((c) => (
              <th key={c} data-testid={`col-${c}`}>
                {c}
                {c === 'Pending at' ? null : (
                  <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}> *</span>
                )}
              </th>
            ))}
            <th /></tr>
        </thead>
        <tbody>
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={13} data-testid="no-rows">
                {rows.length === 0
                  ? 'Nothing has been forwarded to the Branding team yet.'
                  : 'No row matches the filters.'}
              </td>
            </tr>
          ) : null}
          {visibleRows.map((r) => (
            <tr key={r.requestLineId} data-testid={`review-${r.requestLineId}`}>
              <td>
                {releasable.includes(r.requestLineId) ? (
                  <input
                    type="checkbox"
                    aria-label={`select-${r.requestNo}-${r.lineNumber}`}
                    data-testid={`select-${r.requestLineId}`}
                    checked={picked.has(r.requestLineId)}
                    onChange={() => toggle(r.requestLineId)}
                  />
                ) : null}
              </td>
              <td>{r.requestNo}</td>
              <td data-testid={`pending-at-${r.requestLineId}`}>{r.pendingAt ?? '—'}</td>
              <td>{r.companyName}</td>
              <td>{r.districtCode}</td>
              <td>{r.typeName}</td>
              <td>{r.itemName || '—'}</td>
              <td data-testid={`requested-${r.requestLineId}`}>
                {orderLineQuantityLabel(r, r.quantity, decimal)}
              </td>
              <td>
                <input
                  aria-label={`revised-${r.requestNo}-${r.lineNumber}`}
                  data-testid={`revised-${r.requestLineId}`}
                  value={draft[r.requestLineId] ?? ''}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [r.requestLineId]: e.target.value }))
                  }
                  disabled={!isOpen(r.requestLineId)}
                  style={{ width: 90 }}
                />
              </td>
              <td data-testid={`wo-${r.requestLineId}`}>
                {/*
                  An attached order used to be printed as plain text, so the
                  officer who picked the wrong one — or whose order ran short —
                  had no way back. It stays changeable right up until the row is
                  approved or gone to the vendor, which is what the review stage
                  is for. The server holds the same rule, so this is a door, not
                  the lock.
                */}
                {r.woNumber && cover[r.requestLineId] === undefined ? (
                  <>
                    {`${r.woNumber} / ${r.woLineNumber}`}
                    {isOpen(r.requestLineId) ? (
                      <Button
                        type="button"
                        variant="subtle"
                        onClick={() => void loadCover(r)}
                        disabled={busy || !r.brandingItemId}
                        data-testid={`change-wo-${r.requestLineId}`}
                      >
                        Change
                      </Button>
                    ) : null}
                  </>
                ) : !r.woNumber && !isOpen(r.requestLineId) ? (
                  '—'
                ) : cover[r.requestLineId] === undefined ? (
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => void loadCover(r)}
                    disabled={busy || !r.brandingItemId}
                    data-testid={`find-wo-${r.requestLineId}`}
                  >
                    Attach work order
                  </Button>
                ) : cover[r.requestLineId] === 'loading' ? (
                  'Looking…'
                ) : (cover[r.requestLineId] as readonly WorkOrderCoverLine[]).length === 0 ? (
                  <span role="alert" data-testid={`no-wo-${r.requestLineId}`}>
                    None issued — issue a work order first
                  </span>
                ) : (
                  <>
                  <select
                    aria-label={`work-order-${r.requestNo}-${r.lineNumber}`}
                    data-testid={`wo-select-${r.requestLineId}`}
                    defaultValue={r.workOrderLineId ?? ''}
                    onChange={(e) => { if (e.target.value) void attach(r, e.target.value); }}
                    disabled={busy}
                  >
                    <option value="">Select…</option>
                    {(cover[r.requestLineId] as readonly WorkOrderCoverLine[]).map((c) => {
                      // The committed figure alone says what the order promised,
                      // not what is left to give. Both are shown, and what this
                      // line would leave behind, so the choice is made on the
                      // number that decides it.
                      const balance = Number(c.balanceQuantity);
                      // What the server will weigh: a valid typed figure, else
                      // the row's saved draw (revised, else requested).
                      const typed = Number(draft[r.requestLineId]);
                      const asked = typed > 0 ? typed : Number(r.revisedQuantity ?? r.quantity);
                      const after = Number.isFinite(asked) ? balance - asked : null;
                      const uom = c.uomCode ?? '';
                      return (
                        <option key={c.workOrderLineId} value={c.workOrderLineId}>
                          {`${c.woNumber} / ${c.lineNumber} — ${c.vendorCode} — ${decimal(c.committedQuantity)} ${uom} @ ${c.rateValue == null ? '—' : decimal(c.rateValue)}`
                            + ` · left ${decimal(c.balanceQuantity)} ${uom}`
                            + (after == null ? '' : ` · after this ${decimal(after)} ${uom}`)}
                        </option>
                      );
                    })}
                  </select>
                  {r.woNumber ? (
                    <Button
                      type="button"
                      variant="subtle"
                      onClick={() => setCover((c) => {
                        const next = { ...c };
                        delete next[r.requestLineId];
                        return next;
                      })}
                      disabled={busy}
                      data-testid={`keep-wo-${r.requestLineId}`}
                    >
                      Keep {r.woNumber}
                    </Button>
                  ) : null}
                  </>
                )}
              </td>
              {/* What the attached work order line still has free. Its own
                  column so the ceiling is read at a glance beside the figure
                  being revised, not buried in the order's name. */}
              <td data-testid={`wo-left-${r.requestLineId}`}>
                {r.woBalanceQuantity == null
                  ? '—'
                  : `${decimal(r.woBalanceQuantity)} ${r.uomCode ?? ''}`.trim()}
              </td>
              {/* The order names the vendor, so the order is read first —
                  the same order of authority the server releases on. Reading
                  the row's own stamp first showed the vendor it *last* went
                  to even after a different order was attached, so the screen
                  named one vendor while the release would have used another. */}
              <td>
                {r.woVendorName
                  ? `${r.woVendorName}${r.woVendorCode ? ` (${r.woVendorCode})` : ''}`
                  : (r.vendorName ?? '—')}
              </td>
              <td>
                {/* Always rendered, so a row that has left shows the control
                    refusing rather than an empty cell that explains nothing.
                    The reason is the server's own sentence. */}
                {isOpen(r.requestLineId) ? (
                  <>
                    <Button
                      type="button"
                      variant="subtle"
                      onClick={() => void saveRevision(r)}
                      disabled={busy}
                      data-testid={`save-${r.requestLineId}`}
                    >
                      Save qty
                    </Button>
                    <Button
                      type="button"
                      variant="subtle"
                      onClick={() => closeRow(r)}
                      disabled={busy}
                      data-testid={`done-${r.requestLineId}`}
                    >
                      Done
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => openRow(r)}
                    disabled={busy || whyNotModifiable(r) !== null}
                    title={whyNotModifiable(r) ?? undefined}
                    data-testid={`modify-${r.requestLineId}`}
                  >
                    Modify
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <section aria-label="forward-next-level">
        <h2>Forward to next level</h2>
        {/* Owner, 19 Oct: ticking rows and forwarding is the approval. A row
            waiting at this reader's level goes to the next level of its
            workflow; a row whose last level is approved goes to its vendor. */}
        <p data-testid="decidable-count">
          {decidable.length} ticked row{decidable.length === 1 ? '' : 's'} to the next approval level
        </p>
        {/* The one question an officer must never have to guess is where a
            record is actually going. Each ticked row is named here with the
            authority it goes on, in the same order the server applies: the
            work order first, then the vendor the row is already with, and the
            picker only for a row that has neither. */}
        {eligible.byOrder.map((d) => (
          <p key={`o-${d.who}`} data-testid="goes-by-order">
            {d.rows} row{d.rows === 1 ? '' : 's'} → {d.who}, named by the work order.
          </p>
        ))}
        {eligible.byRow.map((d) => (
          <p key={`r-${d.who}`} data-testid="goes-by-row">
            {d.rows} row{d.rows === 1 ? '' : 's'} → {d.who}, the vendor already on the row.
          </p>
        ))}
        {eligible.noVendor > 0 ? (
          <p data-testid="no-vendor-rows">
            {eligible.noVendor} row{eligible.noVendor === 1 ? '' : 's'} name no
            vendor — fill the Vendor column on the row and send it after.
          </p>
        ) : null}
        <p data-testid="picked-count">{picked.size} selected</p>
        <label>
          Remarks (needed to reject){' '}
          <input
            aria-label="decision-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            disabled={busy}
            style={{ width: 280 }}
          />
        </label>{' '}
        <Button
          type="button"
          variant="primary"
          onClick={() => void forwardNext()}
          disabled={busy || picked.size === 0}
          title={picked.size === 0 ? 'Tick the rows to forward' : undefined}
          data-testid="forward-next"
        >
          Forward to next level
        </Button>{' '}
        <Button
          type="button"
          variant="danger"
          onClick={() => void reject()}
          disabled={busy || decidable.length === 0}
          title={decidable.length === 0 ? 'Tick the rows waiting for your decision' : undefined}
          data-testid="reject-selected"
        >
          Reject selected
        </Button>
      </section>
    </main>
  );
}
